import { Box, Table, TableBody, TableCell, TableHead } from "@material-ui/core";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { FC, useEffect, useMemo, useState } from "react";
import { getShiftRecordTableColumns } from "../tableData";
import { shallowEqual, useDispatch } from "react-redux";
import { EnchancedSortLabel } from "components/tables/tableSortLabel";
import { useShiftPageStyles } from "../styles";
import { ShiftRecordRow } from "./ShiftRecordRow";
import { shiftRecordsUpdateFilters } from "actions/shiftTrackerActions";
import { getFilteredShiftRecordIds, getTimeSincePost } from "utils/filters";

interface PropsType {
  tableNumber: number;
  width: number;
}

const ShiftRecordTable: FC<PropsType> = ({ tableNumber, width }) => {
  const classes = useShiftPageStyles();
  const dispatch = useDispatch();

  const rowIds = useTypeSelectors(
    (state) =>
      getFilteredShiftRecordIds(
        state.dasBoard.currentReport,
        state.dasBoard.recordListFilters.search,
        state.dasBoard.recordListFilters.ordering || "-id",
      ),
    shallowEqual,
  );
  const activeColumns = useTypeSelectors((state) => state.dasBoard.recordListFilters.activeColumns);
  const ordering = useTypeSelectors((state) => state.dasBoard.recordListFilters.ordering);
  const currentReportDate = useTypeSelectors((state) => state.dasBoard.currentReportDate);
  const shiftStation = useTypeSelectors((state) => state.dasBoard.currentReportStation.id);
  const hasSecondDriver = useTypeSelectors((state) => state.dasBoard.hasSecondDriver);
  const canCreateOldShifts = useTypeSelectors(
    (state) => state.auth.userData?.can_create_old_shifts,
  );
  const [sortId, setSortId] = useState<string>(ordering || "-id");
  const disabledByDate = getTimeSincePost(currentReportDate);

  const columns = useMemo(
    () => getShiftRecordTableColumns(activeColumns, tableNumber, width, hasSecondDriver),
    [activeColumns, tableNumber, width, hasSecondDriver],
  );

  useEffect(() => {
    setSortId(ordering);
  }, [ordering]);

  const handleSortChange = (sortId: string | undefined, cellId: string) => {
    if (sortId?.replace("-", "") === cellId) {
      const sortString = `${sortId?.includes("-") ? "" : "-"}${cellId}`;
      setSortId(sortString);
      dispatch(shiftRecordsUpdateFilters({ ordering: sortString }));
    } else {
      setSortId(cellId);
      dispatch(shiftRecordsUpdateFilters({ ordering: cellId }));
    }
  };

  return (
    <Table stickyHeader>
      <TableHead>
        {columns.map((col: any) => (
          <TableCell key={col.id} className={`${classes.tableHeadCell} ${classes.fixedHeight}`}>
            <Box className={classes.tableCellContent}>
              <EnchancedSortLabel
                columnId={col.id}
                columnLabel={col.label}
                hasSortLabel={col?.hasSort}
                handleChange={handleSortChange}
                ordering={sortId}
              />
            </Box>
          </TableCell>
        ))}
      </TableHead>
      <TableBody>
        {rowIds.map((rowId, index) => (
          <ShiftRecordRow
            key={rowId}
            index={index}
            rowId={rowId}
            columns={columns}
            classes={classes}
            shiftDate={currentReportDate}
            shiftStation={shiftStation}
            disabledByDate={disabledByDate}
            canCreateOldShifts={!!canCreateOldShifts}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export default ShiftRecordTable;
